/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable import/no-cycle */

import React, {
  createContext,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import i18n from 'i18next';
import api, {
  addToken,
  getCookies,
  removeBearerToken,
  routes,
} from '../api/api';

import { AuthContextType } from '../types/Types';
import { StorageHelper } from '../utils/StorageHelper';
import { LanguageHelper } from '../utils/LanguageHelper';
import { CookieHelper } from '../utils/CookieHelper';
import { UtmManager } from '../utils/UtmManager';
import { LocationHelper } from '../utils/LocationHelper';
import { SessionChecker } from '../utils/SessionChecker';
import { BackMappings } from '../utils/BackMappings';
import { CryptoUtil } from '../utils/CryptoUtil';
import { HostProvider } from '../utils/HostProvider';

type Props = {
  children: ReactElement;
};

export const defaultContextValue = {
  user: {
    idHub: 0,
    emailContacto: '',
    fechaRegistro: '',
    gender: '',
    fechaNacimiento: '',
    store: '',
    idShopper: 0,
    email: '',
    locale: '',
    token: '',
    userName: '',
    moneda: 'euro',
  },
  configAccess: {
    error: false,
    miCuenta: [],
    notificacionPojo: {
      numero: 0,
      numCuponesActivables: 0,
      descripcion: '',
    },
    ventajas: [],
    footerLinks: [],
    locale: i18n.language,
  },
};

export const AuthContext = createContext<AuthContextType>(
  defaultContextValue as AuthContextType,
);

export const AuthProvider: FC<Props> = ({ children }) => {
  const isRecuerdame_literal = 'isRecuerdame';
  const [user, setUser] = useState<AuthContextType>(defaultContextValue);

  const valueContext: AuthContextType = {
    ...user,
  };

  useEffect(() => {
    const logout = () => {
      CookieHelper.removeUserCookiesForLogout();
      removeBearerToken();
    };

    const updateUser = async (token: string) => {
      const params = LocationHelper.getParametersFromUrl();
      if (StorageHelper.getCambioTelefCom() === null) {
        StorageHelper.setCambioTelefCom(params.cambioTelefCom || false);
      }
      if (StorageHelper.getCambioEmailComInv() === null) {
        StorageHelper.setCambioEmailComInv(params.cambioEmailComInv || false);
      }
      if (params.fromApp) {
        StorageHelper.setTarjetaOroPopup(params.altaOroExterna || false);
        StorageHelper.setFromApp(params.fromApp);
      } else if (StorageHelper.isUrlInterna()) {
        console.log(
          'se elimina URL interna, is URL interna',
          StorageHelper.isUrlInterna(),
        );
        StorageHelper.removeUrlInterna();
      } else {
        StorageHelper.setFromApp('');
        StorageHelper.removeUrlInterna();
      }

      const parametros = {
        view: StorageHelper.getOrigin(),
      };
      await api
        .post(routes.me, JSON.stringify(parametros))
        .then((response) => {
          if (
            response.data.error &&
            response.data.errorCode === BackMappings.GO_TO_RELOGIN
          ) {
            SessionChecker.closeSessionAndGoToReLogin(null, i18n.language);
          } else {
            const userParams = CookieHelper.getUserParams();
            if (!userParams) {
              window.location.reload();
            }
            const iscierreMultidispositivoDesdeOtroDispositivo =
              response.data.pojo === undefined && userParams !== undefined;
            if (iscierreMultidispositivoDesdeOtroDispositivo) {
              const origin = StorageHelper.getOrigin();
              SessionChecker.closeSesion(origin);
              document.location.href = `${HostProvider.getHost()}${
                i18n.language
              }/nlrLogin?fromUrl=${origin}`;
            } else {
              const locale = LanguageHelper.getCorrectedLocale(
                response.data.pojo.locale,
                userParams,
                response.data.pojo.isGibraltar,
              );
              LanguageHelper.setLanguage(locale);
              const isApp = StorageHelper.isApp(userParams);
              StorageHelper.setGibraltar(response.data.pojo.isGibraltar);
              StorageHelper.setAndorra(response.data.pojo.isAndorra);
              const objUser: AuthContextType = {
                user: { ...response.data.pojo, token, ...userParams, locale },
              };
              objUser.user.email = CryptoUtil.decryptAes(objUser.user.email);
              objUser.user.emailContacto = CryptoUtil.decryptAes(
                objUser.user.emailContacto,
              );
              objUser.user.fechaNacimiento = CryptoUtil.decryptAes(
                objUser.user.fechaNacimiento,
              );
              objUser.user.fechaRegistro = CryptoUtil.decryptAes(
                objUser.user.fechaRegistro,
              );
              objUser.user.gender = CryptoUtil.decryptAes(objUser.user.gender);
              objUser.user.userName = CryptoUtil.decryptAes(
                objUser.user.userName,
              );
              if (
                objUser.user.card !== null &&
                objUser.user.card !== undefined
              ) {
                objUser.user.card = CryptoUtil.decryptAes(objUser.user.card);
              }
              objUser.user.travelMember = objUser.user.travelMember ? '1' : '0';
              objUser.user.isApp = isApp;
              objUser.user.isRecuerdame =
                userParams[isRecuerdame_literal] === 'true';
              const fb =
                objUser.user.isFB !== undefined ? objUser.user.isFB : false;
              CookieHelper.setFB(fb);
              setUser(objUser);
              CookieHelper.setUserData(objUser);
            }
          }
        })
        .catch((error) => {
          console.error(error);
          logout();
        });
    };

    const setToken = async (token: string, isRecuerdame: boolean) => {
      CookieHelper.setToken(token, isRecuerdame);
      await updateUser(token);
    };

    const updateToken = async (params: {}) => {
      CookieHelper.setUserParams(params);
      await api
        .post(routes.login, params)
        .then((token) => {
          if (token.data) {
            StorageHelper.initializeRevistasVars();
            const isRecuerdame = params[isRecuerdame_literal] === 'true';
            setToken(token.data, isRecuerdame);
            const path = UtmManager.getPathWithUtms(params);
            window.history.replaceState(null, '', path);
          } else {
            LocationHelper.goToBackendForLoginAndRedirect(i18n.language);
          }
        })
        .catch((error) => {
          console.log(error);
          logout();
        });
    };

    LocationHelper.fixRedsysCallbackIframeProblems();

    const token = getCookies().get('token');
    const params = LocationHelper.getParametersFromUrl();
    const thereAreUserParams = StorageHelper.thereAreUserParams(params);
    if (token !== undefined && !thereAreUserParams) {
      addToken(token);
      let path = UtmManager.getPathWithUtms(params);
      path = LocationHelper.addMocksParams(params, path);
      window.history.replaceState(null, '', path);
      updateUser(token);
    } else if (thereAreUserParams) {
      updateToken(params);
    } else if (
      LocationHelper.isControPageVacioIndex()
      // eslint-disable-next-line no-empty
    ) {
    } else {
      LocationHelper.goToBackendForLoginAndRedirect(i18n.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = LocationHelper.getParametersFromUrl();
  LanguageHelper.redirectIfUrlRemainsIncorrectLanguage();

  StorageHelper.setEcommerceBot(params);
  if (params.altaOroExterna) {
    StorageHelper.setTarjetaOroPopup(params.altaOroExterna);
  }
  if (params.modalToUpMisDatos) {
    StorageHelper.setModalToUpMisDatos(params.modalToUpMisDatos);
  }
  if (params.cambioTelefCom) {
    StorageHelper.setCambioTelefCom(params.cambioTelefCom);
  }
  if (params.cambioEmailComInv) {
    StorageHelper.setCambioEmailComInv(params.cambioEmailComInv);
  }
  if (params.scrollBaja) {
    StorageHelper.setScrollBaja(params.scrollBaja);
  }
  if (params.openFormOro) {
    StorageHelper.setOpenFormOro();
  }
  if (params.scrollSuscripciones) {
    StorageHelper.setScrollSuscripciones(params.scrollSuscripciones);
  }
  if (params.scrollPreferencia) {
    StorageHelper.setScrollPreferencia(params.scrollPreferencia);
  }
  if (params.scrollAbrirFacturacion) {
    StorageHelper.setScrollAbrirFacturacion(params.scrollAbrirFacturacion);
  }
  if (params.scrollFacturacion) {
    StorageHelper.setScrollFacturacion(params.scrollFacturacion);
  }
  if (params.tpvResponseMessage) {
    StorageHelper.setTpvResponseMessage(
      decodeURIComponent(params.tpvResponseMessage.replace(/\+/g, ' ')),
    );
  }
  if (params.expiredSessionUrl) {
    StorageHelper.setExpiredSessionUrl(params.expiredSessionUrl);
  }
  if (params.fromUrl) {
    StorageHelper.setFromUrl(params.fromUrl);
  }
  if (params.nuevaDireccionEntrega) {
    StorageHelper.setNuevaDireccionEntrega(params.nuevaDireccionEntrega);
  }
  return (
    <AuthContext.Provider value={valueContext}>{children}</AuthContext.Provider>
  );
};

export function useAuthStore(): AuthContextType {
  return useContext(AuthContext);
}
